import type { ApiClient } from '../client';
import type { AdmitCardAudienceRequest, AdmitCardDashboardStats, CreateCertificateTemplateRequest, CreateCertificateTemplateResponse, ExamTimetableCsvPreviewResponse, ExamTimetableCsvRequest, GenerateAdmitCardsRequest, GenerateAdmitCardsResponse, ImportExamTimetableCsvResponse, IssueCertificateRequest, IssueCertificateResponse, ListCertificateTemplatesResponse, ListExamTimetableResponse, PreviewAdmitCardEligibilityResponse, PurgeExpiredCertificatesResponse, RenderCertificateResponse, SaveExamTimetableRequest, Uuid, UpdateCertificateTemplateRequest, UpdateCertificateTemplateResponse } from '../types';
import type { ApiResponse } from '../types';
import { buildExamTimetableQuery } from '../../../utils/examTimetableCsv';

export function bindCertificates(client: ApiClient) {
  return {
async listCertificateTemplates(certificateType?: string) {
    const qs = certificateType ? `?certificate_type=${encodeURIComponent(certificateType)}` : '';
    return client.request<ListCertificateTemplatesResponse>(`/certificates/templates${qs}`);
  },

async createCertificateTemplate(body: CreateCertificateTemplateRequest) {
    return client.request<CreateCertificateTemplateResponse>('/certificates/templates', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async updateCertificateTemplate(templateId: Uuid, body: UpdateCertificateTemplateRequest) {
    return client.request<UpdateCertificateTemplateResponse>(`/certificates/templates/${encodeURIComponent(templateId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

async deleteCertificateTemplate(templateId: Uuid) {
    return client.request<void>(`/certificates/templates/${encodeURIComponent(templateId)}`, {
      method: 'DELETE',
    });
  },

async issueCertificate(body: IssueCertificateRequest) {
    return client.request<IssueCertificateResponse>('/certificates/issue', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async previewAdmitCardEligibility(body: AdmitCardAudienceRequest) {
    return client.request<PreviewAdmitCardEligibilityResponse>(
      '/certificates/admit-cards/preview-eligibility',
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
  },

async generateAdmitCards(body: GenerateAdmitCardsRequest) {
    const payload: Record<string, unknown> = { ...body };
    for (const key of Object.keys(payload)) {
      const v = payload[key];
      if (v === null || v === undefined) delete payload[key];
    }
    return client.request<GenerateAdmitCardsResponse>('/certificates/admit-cards/generate', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

async getAdmitCardDashboardStats() {
    return client.request<AdmitCardDashboardStats>('/certificates/admit-cards/dashboard-stats');
  },

async renderCertificate(certificateId: Uuid) {
    return client.request<RenderCertificateResponse>(
      `/certificates/${encodeURIComponent(certificateId)}/render`
    );
  },

async downloadCertificatePdf(certificateId: Uuid): Promise<Blob> {
    const path = `/certificates/${encodeURIComponent(certificateId)}/pdf`;
    const url = `${client.baseUrl}${path}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: client.authHeaders(),
    });
    if (!response.ok) {
      throw new Error(`PDF download failed: ${response.status}`);
    }
    return response.blob();
  },

async purgeExpiredCertificates(): Promise<ApiResponse<PurgeExpiredCertificatesResponse>> {
    return client.request<PurgeExpiredCertificatesResponse>('/certificates/purge-expired', {
      method: 'POST',
    });
  },

async deleteCertificate(certificateId: Uuid): Promise<ApiResponse<void>> {
    return client.request<void>(`/certificates/${encodeURIComponent(certificateId)}`, {
      method: 'DELETE',
    });
  },

async listExamTimetable(params: {
    examId: string;
    term: string;
    academicYear: string;
    classId?: string;
  }) {
    const qs = buildExamTimetableQuery(params);
    return client.request<ListExamTimetableResponse>(`/certificates/exam-timetable?${qs}`);
  },

async downloadExamTimetableTemplate(params: {
    examId: string;
    term: string;
    academicYear: string;
    classId?: string;
    examDate?: string;
  }): Promise<string> {
    const qs = buildExamTimetableQuery(params);
    const url = `${client.baseUrl}/certificates/exam-timetable/template.csv?${qs}`;
    const response = await fetch(url, { headers: client.authHeaders() });
    if (!response.ok) {
      throw new Error(`Template download failed: ${response.status}`);
    }
    return response.text();
  },

async previewExamTimetableCsv(body: ExamTimetableCsvRequest) {
    return client.request<ExamTimetableCsvPreviewResponse>('/certificates/exam-timetable/preview-csv', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async importExamTimetableCsv(body: ExamTimetableCsvRequest) {
    return client.request<ImportExamTimetableCsvResponse>('/certificates/exam-timetable/import-csv', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

async saveExamTimetable(body: SaveExamTimetableRequest) {
    return client.request<ListExamTimetableResponse>('/certificates/exam-timetable', {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }
  };
}
